import { useState, useEffect, useCallback, useRef } from "react";
import axios from "axios";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import {
  Users, CreditCard, BarChart3, LogOut, Search, Plus,
  RefreshCw, Trash2, CheckCircle2, XCircle, Clock, AlertTriangle,
  ChevronDown, X, ShieldOff, Pencil, Radio, Wifi, WifiOff, KeyRound,
  Mail, Send, SlidersHorizontal, Loader2,
} from "lucide-react";
import { useConfirm } from "../components/Confirm";

const BASE = process.env.REACT_APP_BACKEND_URL + "/api";

function adminApi() {
  const token = localStorage.getItem("sw_admin_token");
  return axios.create({
    baseURL: BASE,
    headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
  });
}

// ---- Status badge ----
const STATUS_META = {
  trial:         { label: "Trial",          cls: "bg-blue-50 text-blue-700 border-blue-200" },
  trial_expired: { label: "Trial Expired",  cls: "bg-red-50 text-red-700 border-red-200" },
  active:        { label: "Active",         cls: "bg-emerald-50 text-emerald-700 border-emerald-200" },
  expired:       { label: "Expired",        cls: "bg-red-50 text-red-700 border-red-200" },
  suspended:     { label: "Suspended",      cls: "bg-slate-100 text-slate-500 border-slate-200" },
};
function StatusBadge({ status }) {
  const m = STATUS_META[status] || { label: status, cls: "bg-slate-100 text-slate-600" };
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-semibold border ${m.cls}`}>
      {m.label}
    </span>
  );
}

function fmt(iso) {
  if (!iso) return "—";
  return new Date(iso).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
}

// ==================== Assign Plan Modal ====================
function AssignPlanModal({ user, plans, onClose, onSaved }) {
  const [planId, setPlanId] = useState("");
  const [saving, setSaving] = useState(false);

  const selectedPlan = plans.find((p) => p.id === planId);
  const expiresAt = planId && selectedPlan
    ? new Date(Date.now() + selectedPlan.duration_days * 86400_000).toISOString()
    : null;

  const handleSave = async () => {
    if (!planId || !expiresAt) { toast.error("Select a plan"); return; }
    setSaving(true);
    try {
      const { data } = await adminApi().patch(`/admin/users/${user.id}`, {
        plan_id: planId,
        plan_expires_at: expiresAt,
      });
      toast.success("Plan assigned");
      onSaved(data);
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to assign plan");
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 grid place-items-center p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md p-6">
        <div className="flex items-center justify-between mb-5">
          <h3 className="text-[15px] font-bold text-slate-900">Assign Subscription Plan</h3>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-700"><X size={18} /></button>
        </div>
        <p className="text-[13px] text-slate-600 mb-4">User: <span className="font-semibold">{user.name}</span> ({user.email})</p>

        <div className="mb-4">
          <label className="block text-[11px] font-bold uppercase tracking-wider text-slate-500 mb-1.5">Select Plan</label>
          <select
            value={planId}
            onChange={(e) => setPlanId(e.target.value)}
            className="w-full h-10 px-3 border border-slate-200 rounded-xl text-[13.5px] focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="">— choose a plan —</option>
            {plans.filter((p) => p.is_active).map((p) => (
              <option key={p.id} value={p.id}>
                {p.name} — {p.currency} {p.price} / {p.duration_days}d
              </option>
            ))}
          </select>
        </div>

        {selectedPlan && (
          <div className="bg-blue-50 border border-blue-100 rounded-xl p-3 mb-4 text-[12.5px] text-blue-800">
            Subscription will be active until <strong>{fmt(expiresAt)}</strong> ({selectedPlan.duration_days} days from today).
          </div>
        )}

        <div className="flex gap-3 mt-2">
          <button onClick={onClose} className="flex-1 h-9 border border-slate-200 rounded-xl text-[13px] font-semibold text-slate-600 hover:bg-slate-50 transition">Cancel</button>
          <button onClick={handleSave} disabled={saving || !planId} className="flex-1 h-9 bg-blue-600 text-white rounded-xl text-[13px] font-semibold hover:bg-blue-700 disabled:opacity-50 transition">
            {saving ? "Saving…" : "Assign Plan"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ==================== Extend Trial Modal ====================
function ExtendTrialModal({ user, onClose, onSaved }) {
  const [days, setDays] = useState(14);
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    setSaving(true);
    try {
      const { data } = await adminApi().patch(`/admin/users/${user.id}`, { extend_trial_days: days });
      toast.success(`Trial extended by ${days} days`);
      onSaved(data);
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to extend trial");
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 grid place-items-center p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm p-6">
        <div className="flex items-center justify-between mb-5">
          <h3 className="text-[15px] font-bold text-slate-900">Extend Trial</h3>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-700"><X size={18} /></button>
        </div>
        <p className="text-[13px] text-slate-600 mb-4">User: <span className="font-semibold">{user.name}</span></p>

        <div className="mb-4">
          <label className="block text-[11px] font-bold uppercase tracking-wider text-slate-500 mb-2">Days to add</label>
          <div className="flex gap-2 mb-3">
            {[7, 14, 30].map((d) => (
              <button key={d} onClick={() => setDays(d)}
                className={`flex-1 h-9 rounded-xl text-[13px] font-semibold border transition ${days === d ? "bg-slate-900 text-white border-slate-900" : "border-slate-200 text-slate-600 hover:border-slate-400"}`}>
                +{d}d
              </button>
            ))}
          </div>
          <input type="number" min={1} max={365} value={days} onChange={(e) => setDays(Number(e.target.value))}
            className="w-full h-9 px-3 border border-slate-200 rounded-xl text-[13.5px] focus:outline-none focus:ring-2 focus:ring-blue-500" />
        </div>

        <div className="flex gap-3">
          <button onClick={onClose} className="flex-1 h-9 border border-slate-200 rounded-xl text-[13px] font-semibold text-slate-600 hover:bg-slate-50 transition">Cancel</button>
          <button onClick={handleSave} disabled={saving || days < 1} className="flex-1 h-9 bg-slate-900 text-white rounded-xl text-[13px] font-semibold hover:bg-slate-700 disabled:opacity-50 transition">
            {saving ? "Saving…" : "Extend"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ==================== Dashboard Tab ====================
function DashboardTab({ stats, loading }) {
  if (loading) return <div className="py-20 text-center text-slate-400 text-sm">Loading stats…</div>;
  if (!stats) return null;

  const cards = [
    { label: "Total Users",     value: stats.total_users,     icon: Users,        color: "text-blue-600 bg-blue-50" },
    { label: "On Trial",        value: stats.trial_users,     icon: Clock,        color: "text-amber-600 bg-amber-50" },
    { label: "Active",          value: stats.active_users,    icon: CheckCircle2, color: "text-emerald-600 bg-emerald-50" },
    { label: "Expired",         value: stats.expired_users,   icon: XCircle,      color: "text-red-600 bg-red-50" },
    { label: "Suspended",       value: stats.suspended_users, icon: ShieldOff,    color: "text-slate-600 bg-slate-100" },
    { label: "Expiring ≤7 days",value: stats.expiring_soon,   icon: AlertTriangle, color: "text-orange-600 bg-orange-50" },
    { label: "New (7 days)",    value: stats.new_users_7d,    icon: Users,        color: "text-violet-600 bg-violet-50" },
    { label: "New (30 days)",   value: stats.new_users_30d,   icon: Users,        color: "text-slate-600 bg-slate-100" },
    { label: "Total Messages",  value: stats.total_messages,  icon: BarChart3,    color: "text-sky-600 bg-sky-50" },
    { label: "Total Tasks",     value: stats.total_tasks,     icon: BarChart3,    color: "text-teal-600 bg-teal-50" },
  ];

  return (
    <div>
      <h2 className="text-[13px] font-bold uppercase tracking-wider text-slate-500 mb-4">Platform Overview</h2>
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
        {cards.map(({ label, value, icon: Icon, color }) => (
          <div key={label} className="bg-white border border-slate-200 rounded-2xl p-4">
            <div className={`w-9 h-9 rounded-xl grid place-items-center mb-3 ${color}`}>
              <Icon size={17} />
            </div>
            <div className="text-2xl font-black text-slate-900">{value?.toLocaleString()}</div>
            <div className="text-[12px] text-slate-500 mt-0.5">{label}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ==================== Users Tab ====================
function UsersTab({ users, plans, loading, onRefresh }) {
  const confirm = useConfirm();
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [actionUser, setActionUser] = useState(null);       // { user, type: "assign"|"extend"|"suspend" }
  const [expandedId, setExpandedId] = useState(null);
  const [localUsers, setLocalUsers] = useState(users);

  useEffect(() => { setLocalUsers(users); }, [users]);

  // Renewals = paying customers who need attention now: active plan ending
  // within 14 days, or a paid plan that already lapsed.
  const isRenewal = (u) => {
    const s = u.subscription || {};
    return (s.status === "active" && s.days_remaining <= 14) || s.status === "expired";
  };

  const SEGMENTS = [
    { key: "",              label: "All",           match: () => true },
    { key: "active",        label: "Active",        match: (u) => u.subscription?.status === "active" },
    { key: "renewal",       label: "Renewals",      match: isRenewal },
    { key: "trial",         label: "Trial",         match: (u) => u.subscription?.status === "trial" },
    { key: "trial_expired", label: "Trial Expired", match: (u) => u.subscription?.status === "trial_expired" },
    { key: "suspended",     label: "Suspended",     match: (u) => u.subscription?.status === "suspended" },
  ];
  const activeSegment = SEGMENTS.find((s) => s.key === statusFilter) || SEGMENTS[0];

  const filtered = localUsers.filter((u) => {
    const q = search.toLowerCase();
    const matchSearch = !q || u.name?.toLowerCase().includes(q) || u.email?.toLowerCase().includes(q);
    return matchSearch && activeSegment.match(u);
  });

  const handleUserSaved = (updated) => {
    setLocalUsers((prev) => prev.map((u) => u.id === updated.id ? { ...u, ...updated } : u));
  };

  const handleSuspend = async (user) => {
    const ok = await confirm({
      title: `Suspend ${user.name}?`,
      message: "They lose access immediately. You can reactivate later.",
      confirmLabel: "Suspend",
      tone: "danger",
    });
    if (!ok) return;
    try {
      const { data } = await adminApi().patch(`/admin/users/${user.id}`, { subscription_status: "suspended" });
      handleUserSaved(data);
      toast.success("User suspended");
    } catch { toast.error("Failed"); }
  };

  const handleReactivate = async (user) => {
    const ok = await confirm({
      title: `Reactivate ${user.name}?`,
      message: "They'll regain access at their previous plan / trial level.",
      confirmLabel: "Reactivate",
    });
    if (!ok) return;
    try {
      const { data } = await adminApi().patch(`/admin/users/${user.id}`, { subscription_status: "reactivate" });
      handleUserSaved(data);
      toast.success("User reactivated");
    } catch { toast.error("Failed"); }
  };

  const handleDelete = async (user) => {
    const ok = await confirm({
      title: `Delete ${user.name}'s account permanently?`,
      message: "All their messages, tasks, leads, team and settings are erased. Payments records are kept. This cannot be undone.",
      confirmLabel: "Delete forever",
      tone: "danger",
    });
    if (!ok) return;
    try {
      await adminApi().delete(`/admin/users/${user.id}`);
      setLocalUsers((prev) => prev.filter((x) => x.id !== user.id));
      toast.success("Account deleted");
      onRefresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to delete account");
    }
  };

  return (
    <div>
      {/* Segment chips */}
      <div className="flex flex-wrap gap-1.5 mb-4">
        {SEGMENTS.map((s) => {
          const count = s.key === "" ? localUsers.length : localUsers.filter(s.match).length;
          const active = statusFilter === s.key;
          return (
            <button
              key={s.key}
              onClick={() => setStatusFilter(s.key)}
              className={`h-8 px-3 rounded-full text-[12px] font-semibold border transition ${
                active
                  ? "bg-slate-900 text-white border-slate-900"
                  : "bg-white text-slate-600 border-slate-200 hover:border-slate-400"
              }`}
            >
              {s.label}
              <span className={`ml-1.5 ${active ? "text-slate-300" : "text-slate-400"}`}>{count}</span>
            </button>
          );
        })}
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-3 mb-5">
        <div className="relative flex-1 min-w-[200px]">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <input
            value={search} onChange={(e) => setSearch(e.target.value)}
            placeholder="Search name or email…"
            className="w-full h-9 pl-9 pr-3 border border-slate-200 rounded-xl text-[13px] focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>
        <button onClick={onRefresh} className="h-9 px-3 border border-slate-200 rounded-xl text-slate-500 hover:bg-slate-50 transition">
          <RefreshCw size={14} />
        </button>
      </div>

      <div className="text-[12px] text-slate-400 mb-3">{filtered.length} user{filtered.length !== 1 ? "s" : ""}</div>

      {loading ? (
        <div className="py-12 text-center text-slate-400 text-sm">Loading users…</div>
      ) : filtered.length === 0 ? (
        <div className="py-12 text-center text-slate-400 text-sm">No users found</div>
      ) : (
        <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden">
          {/* Table header */}
          <div className="hidden md:grid grid-cols-[1fr_180px_110px_80px_80px_170px] gap-4 px-4 py-2.5 bg-slate-50 border-b border-slate-100 text-[11px] font-bold uppercase tracking-wider text-slate-500">
            <div>User</div>
            <div>Plan / Expiry</div>
            <div>Status</div>
            <div>Msgs</div>
            <div>Tasks</div>
            <div>Actions</div>
          </div>

          {filtered.map((u, i) => {
            const sub = u.subscription || {};
            const isExpanded = expandedId === u.id;
            return (
              <div key={u.id} className={`border-b border-slate-100 last:border-0 ${i % 2 === 0 ? "" : "bg-slate-50/50"}`}>
                <div className="grid md:grid-cols-[1fr_180px_110px_80px_80px_170px] gap-4 px-4 py-3 items-center">
                  {/* User info */}
                  <div className="min-w-0">
                    <div className="font-semibold text-[13.5px] text-slate-900 truncate">{u.name}</div>
                    <div className="text-[12px] text-slate-500 truncate">{u.email}</div>
                    <div className="text-[11px] text-slate-400 mt-0.5">Joined {fmt(u.created_at)}</div>
                  </div>

                  {/* Plan */}
                  <div className="hidden md:block">
                    <div className="text-[13px] font-semibold text-slate-800">{u.plan_name}</div>
                    <div className="text-[11.5px] text-slate-500">
                      {sub.expires_at ? `Until ${fmt(sub.expires_at)}` : "—"}
                    </div>
                    {sub.days_remaining > 0 && sub.days_remaining < 9999 && (
                      <div className={`text-[11px] font-semibold ${sub.days_remaining <= 5 ? "text-red-500" : "text-slate-400"}`}>
                        {sub.days_remaining}d left
                      </div>
                    )}
                  </div>

                  {/* Status */}
                  <div className="hidden md:block">
                    <StatusBadge status={sub.status} />
                  </div>

                  {/* Counts */}
                  <div className="hidden md:block text-[13px] text-slate-700 font-semibold">{u.message_count?.toLocaleString()}</div>
                  <div className="hidden md:block text-[13px] text-slate-700 font-semibold">{u.task_count?.toLocaleString()}</div>

                  {/* Actions */}
                  <div className="flex items-center gap-1">
                    <button
                      onClick={() => setActionUser({ user: u, type: "assign" })}
                      title="Assign plan"
                      className="h-7 px-2 text-[11.5px] font-semibold bg-blue-50 text-blue-700 hover:bg-blue-100 rounded-lg transition"
                    >
                      Plan
                    </button>
                    <button
                      onClick={() => setActionUser({ user: u, type: "extend" })}
                      title="Extend trial"
                      className="h-7 px-2 text-[11.5px] font-semibold bg-amber-50 text-amber-700 hover:bg-amber-100 rounded-lg transition"
                    >
                      Trial
                    </button>
                    {sub.status === "suspended" ? (
                      <button
                        onClick={() => handleReactivate(u)}
                        title="Reactivate user"
                        className="h-7 px-2 text-[11.5px] font-semibold bg-emerald-50 text-emerald-700 hover:bg-emerald-100 rounded-lg transition"
                      >
                        Reactivate
                      </button>
                    ) : (
                      <button
                        onClick={() => handleSuspend(u)}
                        title="Suspend user"
                        className="h-7 w-7 grid place-items-center text-slate-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition"
                      >
                        <XCircle size={14} />
                      </button>
                    )}
                    <button
                      onClick={() => handleDelete(u)}
                      title="Delete account permanently"
                      className="h-7 w-7 grid place-items-center text-slate-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition"
                    >
                      <Trash2 size={14} />
                    </button>
                    <button
                      onClick={() => setExpandedId(isExpanded ? null : u.id)}
                      className="h-7 w-7 grid place-items-center text-slate-400 hover:text-slate-700 rounded-lg transition"
                    >
                      <ChevronDown size={14} className={`transition-transform ${isExpanded ? "rotate-180" : ""}`} />
                    </button>
                  </div>
                </div>

                {/* Expanded detail row (mobile + extra info) */}
                {isExpanded && (
                  <div className="px-4 pb-3 grid grid-cols-2 md:grid-cols-4 gap-3 border-t border-slate-100 pt-3 bg-slate-50">
                    <div>
                      <div className="text-[10.5px] font-bold uppercase tracking-wider text-slate-400 mb-0.5">Status</div>
                      <StatusBadge status={sub.status} />
                    </div>
                    <div>
                      <div className="text-[10.5px] font-bold uppercase tracking-wider text-slate-400 mb-0.5">Plan</div>
                      <div className="text-[13px] font-semibold text-slate-800">{u.plan_name}</div>
                    </div>
                    <div>
                      <div className="text-[10.5px] font-bold uppercase tracking-wider text-slate-400 mb-0.5">Messages</div>
                      <div className="text-[13px] font-semibold text-slate-800">{u.message_count?.toLocaleString()}</div>
                    </div>
                    <div>
                      <div className="text-[10.5px] font-bold uppercase tracking-wider text-slate-400 mb-0.5">Tasks</div>
                      <div className="text-[13px] font-semibold text-slate-800">{u.task_count?.toLocaleString()}</div>
                    </div>
                    {sub.expires_at && (
                      <div>
                        <div className="text-[10.5px] font-bold uppercase tracking-wider text-slate-400 mb-0.5">Expiry</div>
                        <div className="text-[13px] text-slate-800">{fmt(sub.expires_at)}</div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Modals */}
      {actionUser?.type === "assign" && (
        <AssignPlanModal
          user={actionUser.user}
          plans={plans}
          onClose={() => setActionUser(null)}
          onSaved={handleUserSaved}
        />
      )}
      {actionUser?.type === "extend" && (
        <ExtendTrialModal
          user={actionUser.user}
          onClose={() => setActionUser(null)}
          onSaved={handleUserSaved}
        />
      )}
    </div>
  );
}

// ==================== Edit Plan Modal ====================
function EditPlanModal({ plan, onClose, onSaved }) {
  const [form, setForm] = useState({
    name: plan.name || "",
    price: plan.price?.toString() || "",
    currency: plan.currency || "INR",
    duration_days: plan.duration_days || 30,
    description: plan.description || "",
    features: (plan.features || []).join("\n"),
  });
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    if (!form.name.trim() || !form.price || !form.duration_days) {
      toast.error("Name, price, and duration are required");
      return;
    }
    setSaving(true);
    try {
      const { data } = await adminApi().patch(`/admin/plans/${plan.id}`, {
        name: form.name.trim(),
        price: parseFloat(form.price),
        currency: form.currency,
        duration_days: parseInt(form.duration_days),
        description: form.description.trim() || null,
        features: form.features ? form.features.split("\n").map((s) => s.trim()).filter(Boolean) : [],
      });
      toast.success("Plan updated");
      onSaved(data);
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to update plan");
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 grid place-items-center p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg p-6 overflow-y-auto max-h-[90vh]">
        <div className="flex items-center justify-between mb-5">
          <h3 className="text-[15px] font-bold text-slate-900">Edit Plan</h3>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-700"><X size={18} /></button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
          <div>
            <label className="block text-[11px] font-bold uppercase tracking-wider text-slate-500 mb-1">Plan Name</label>
            <input value={form.name} onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))}
              className="w-full h-9 px-3 border border-slate-200 rounded-xl text-[13px] focus:outline-none focus:ring-2 focus:ring-blue-500" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-[11px] font-bold uppercase tracking-wider text-slate-500 mb-1">Price</label>
              <input type="number" value={form.price} onChange={(e) => setForm((p) => ({ ...p, price: e.target.value }))}
                className="w-full h-9 px-3 border border-slate-200 rounded-xl text-[13px] focus:outline-none focus:ring-2 focus:ring-blue-500" />
            </div>
            <div>
              <label className="block text-[11px] font-bold uppercase tracking-wider text-slate-500 mb-1">Currency</label>
              <select value={form.currency} onChange={(e) => setForm((p) => ({ ...p, currency: e.target.value }))}
                className="w-full h-9 px-3 border border-slate-200 rounded-xl text-[13px] focus:outline-none focus:ring-2 focus:ring-blue-500">
                <option>INR</option><option>USD</option><option>EUR</option>
              </select>
            </div>
          </div>
          <div>
            <label className="block text-[11px] font-bold uppercase tracking-wider text-slate-500 mb-1">Duration (days)</label>
            <div className="flex gap-2">
              {[30, 90, 365].map((d) => (
                <button key={d} onClick={() => setForm((p) => ({ ...p, duration_days: d }))}
                  className={`flex-1 h-9 rounded-xl text-[12.5px] font-semibold border transition ${form.duration_days === d ? "bg-slate-900 text-white border-slate-900" : "border-slate-200 text-slate-600 hover:border-slate-400"}`}>
                  {d === 30 ? "Monthly" : d === 90 ? "Quarterly" : "Yearly"}
                </button>
              ))}
            </div>
          </div>
          <div>
            <label className="block text-[11px] font-bold uppercase tracking-wider text-slate-500 mb-1">Description</label>
            <input value={form.description} onChange={(e) => setForm((p) => ({ ...p, description: e.target.value }))}
              className="w-full h-9 px-3 border border-slate-200 rounded-xl text-[13px] focus:outline-none focus:ring-2 focus:ring-blue-500" />
          </div>
          <div className="md:col-span-2">
            <label className="block text-[11px] font-bold uppercase tracking-wider text-slate-500 mb-1">Features (one per line)</label>
            <textarea value={form.features} onChange={(e) => setForm((p) => ({ ...p, features: e.target.value }))}
              rows={4}
              className="w-full px-3 py-2 border border-slate-200 rounded-xl text-[13px] focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none" />
          </div>
        </div>

        <div className="flex gap-3">
          <button onClick={onClose} className="flex-1 h-9 border border-slate-200 rounded-xl text-[13px] font-semibold text-slate-600 hover:bg-slate-50 transition">Cancel</button>
          <button onClick={handleSave} disabled={saving} className="flex-1 h-9 bg-blue-600 text-white rounded-xl text-[13px] font-semibold hover:bg-blue-700 disabled:opacity-50 transition">
            {saving ? "Saving…" : "Save Changes"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ==================== Live Tab ====================
function WaStatusBadge({ status }) {
  const map = {
    connected:   { label: "Connected",    cls: "bg-emerald-50 text-emerald-700 border-emerald-200" },
    qr:          { label: "QR shown",     cls: "bg-blue-50 text-blue-700 border-blue-200" },
    connecting:  { label: "Connecting",   cls: "bg-amber-50 text-amber-700 border-amber-200" },
    disconnected:{ label: "Disconnected", cls: "bg-slate-100 text-slate-500 border-slate-200" },
    no_session:  { label: "No session",   cls: "bg-slate-50 text-slate-400 border-slate-100" },
  };
  const m = map[status] || { label: status, cls: "bg-slate-100 text-slate-500 border-slate-200" };
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-semibold border ${m.cls}`}>
      {status === "connected" ? <Wifi size={10} /> : <WifiOff size={10} />}
      {m.label}
    </span>
  );
}

function timeSince(iso) {
  if (!iso) return "—";
  const secs = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
  if (secs < 60) return `${secs}s ago`;
  if (secs < 3600) return `${Math.floor(secs / 60)}m ago`;
  return `${Math.floor(secs / 3600)}h ago`;
}

function LiveTab() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const timerRef = useRef(null);

  const load = useCallback(async () => {
    try {
      const { data: d } = await adminApi().get("/admin/live");
      setData(d);
    } catch {
      toast.error("Failed to load live data");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
    timerRef.current = setInterval(load, 30_000);
    return () => clearInterval(timerRef.current);
  }, [load]);

  if (loading) return <div className="py-20 text-center text-slate-400 text-sm">Loading…</div>;

  const { online_users = [], online_count = 0, wa_connected_count = 0, bridge_reachable = true } = data || {};

  return (
    <div>
      {/* Summary cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <div className="bg-white border border-slate-200 rounded-2xl p-4">
          <div className="w-9 h-9 rounded-xl grid place-items-center mb-3 text-emerald-600 bg-emerald-50">
            <Users size={17} />
          </div>
          <div className="text-2xl font-black text-slate-900">{online_count}</div>
          <div className="text-[12px] text-slate-500 mt-0.5">Users online (last 5 min)</div>
        </div>
        <div className="bg-white border border-slate-200 rounded-2xl p-4">
          <div className="w-9 h-9 rounded-xl grid place-items-center mb-3 text-blue-600 bg-blue-50">
            <Wifi size={17} />
          </div>
          <div className="text-2xl font-black text-slate-900">{wa_connected_count}</div>
          <div className="text-[12px] text-slate-500 mt-0.5">WhatsApp sessions active</div>
        </div>
        <div className="bg-white border border-slate-200 rounded-2xl p-4 col-span-2">
          <div className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-xl text-[12px] font-semibold border ${bridge_reachable ? "bg-emerald-50 border-emerald-200 text-emerald-700" : "bg-red-50 border-red-200 text-red-700"}`}>
            <span className={`w-2 h-2 rounded-full ${bridge_reachable ? "bg-emerald-500 animate-pulse" : "bg-red-500"}`} />
            WhatsApp Bridge: {bridge_reachable ? "Online" : "Offline"}
          </div>
          <div className="text-[11px] text-slate-400 mt-2">Auto-refreshes every 30s</div>
        </div>
      </div>

      {/* Online users table */}
      <div className="flex items-center justify-between mb-3">
        <h2 className="text-[13px] font-bold uppercase tracking-wider text-slate-500">Online Users</h2>
        <button
          onClick={() => { setLoading(true); load(); }}
          className="flex items-center gap-1.5 text-[12.5px] text-slate-500 hover:text-slate-900 transition"
        >
          <RefreshCw size={13} /> Refresh now
        </button>
      </div>

      {online_users.length === 0 ? (
        <div className="bg-white border border-slate-200 rounded-2xl p-10 text-center text-slate-400 text-[13.5px]">
          No users active in the last 5 minutes.
        </div>
      ) : (
        <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden">
          <table className="w-full text-[13px]">
            <thead>
              <tr className="border-b border-slate-100 bg-slate-50 text-[11px] font-bold uppercase tracking-wider text-slate-500">
                <th className="text-left px-4 py-3">User</th>
                <th className="text-left px-4 py-3">Subscription</th>
                <th className="text-left px-4 py-3">Last Seen</th>
                <th className="text-left px-4 py-3">WhatsApp</th>
              </tr>
            </thead>
            <tbody>
              {online_users.map((u) => (
                <tr key={u.id} className="border-b border-slate-50 hover:bg-slate-50/60 transition">
                  <td className="px-4 py-3">
                    <div className="font-semibold text-slate-900 leading-tight">{u.name || "—"}</div>
                    <div className="text-[11.5px] text-slate-400">{u.email}</div>
                  </td>
                  <td className="px-4 py-3">
                    <StatusBadge status={u.subscription_status} />
                  </td>
                  <td className="px-4 py-3 text-slate-500">{timeSince(u.last_seen_at)}</td>
                  <td className="px-4 py-3">
                    <WaStatusBadge status={u.wa_status} />
                    {u.wa_connected && u.wa_chats_count > 0 && (
                      <span className="ml-1.5 text-[10.5px] text-slate-400">{u.wa_chats_count} chats</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ==================== Plans Tab ====================
function PlansTab({ plans, onRefresh }) {
  const confirm = useConfirm();
  const [showAdd, setShowAdd] = useState(false);
  const [form, setForm] = useState({ name: "", price: "", currency: "INR", duration_days: 30, description: "", features: "", is_active: true });
  const [saving, setSaving] = useState(false);
  const [localPlans, setLocalPlans] = useState(plans);
  const [editPlan, setEditPlan] = useState(null);

  useEffect(() => { setLocalPlans(plans); }, [plans]);

  const handleCreate = async () => {
    if (!form.name.trim() || !form.price || !form.duration_days) {
      toast.error("Name, price, and duration are required");
      return;
    }
    setSaving(true);
    try {
      const { data } = await adminApi().post("/admin/plans", {
        ...form,
        price: parseFloat(form.price),
        duration_days: parseInt(form.duration_days),
        features: form.features ? form.features.split("\n").map((s) => s.trim()).filter(Boolean) : [],
      });
      setLocalPlans((prev) => [...prev, data]);
      setForm({ name: "", price: "", currency: "INR", duration_days: 30, description: "", features: "", is_active: true });
      setShowAdd(false);
      toast.success("Plan created");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to create plan");
    } finally { setSaving(false); }
  };

  const handleToggleActive = async (plan) => {
    try {
      const { data } = await adminApi().patch(`/admin/plans/${plan.id}`, { is_active: !plan.is_active });
      setLocalPlans((prev) => prev.map((p) => p.id === plan.id ? data : p));
      toast.success(data.is_active ? "Plan activated" : "Plan deactivated");
    } catch { toast.error("Failed"); }
  };

  const handleDelete = async (plan) => {
    const ok = await confirm({
      title: `Delete plan "${plan.name}"?`,
      message: "This cannot be undone. Existing subscribers stay on their current plan.",
      confirmLabel: "Delete",
      tone: "danger",
    });
    if (!ok) return;
    try {
      await adminApi().delete(`/admin/plans/${plan.id}`);
      setLocalPlans((prev) => prev.filter((p) => p.id !== plan.id));
      toast.success("Plan deleted");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to delete plan");
    }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-5">
        <div>
          <h2 className="text-[13px] font-bold uppercase tracking-wider text-slate-500">Subscription Plans</h2>
          <p className="text-[12.5px] text-slate-400 mt-0.5">Manage plans available to users. Payment gateway will be connected later.</p>
        </div>
        <button
          onClick={() => setShowAdd((v) => !v)}
          className="h-9 px-4 bg-slate-900 text-white rounded-xl text-[13px] font-semibold hover:bg-slate-700 transition flex items-center gap-1.5"
        >
          <Plus size={14} /> Add Plan
        </button>
      </div>

      {/* Add plan form */}
      {showAdd && (
        <div className="bg-white border border-slate-200 rounded-2xl p-5 mb-5">
          <h3 className="text-[14px] font-bold text-slate-800 mb-4">New Plan</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
            <div>
              <label className="block text-[11px] font-bold uppercase tracking-wider text-slate-500 mb-1">Plan Name</label>
              <input value={form.name} onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))}
                placeholder="e.g. Starter, Pro, Enterprise"
                className="w-full h-9 px-3 border border-slate-200 rounded-xl text-[13px] focus:outline-none focus:ring-2 focus:ring-blue-500" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-[11px] font-bold uppercase tracking-wider text-slate-500 mb-1">Price</label>
                <input type="number" value={form.price} onChange={(e) => setForm((p) => ({ ...p, price: e.target.value }))}
                  placeholder="999"
                  className="w-full h-9 px-3 border border-slate-200 rounded-xl text-[13px] focus:outline-none focus:ring-2 focus:ring-blue-500" />
              </div>
              <div>
                <label className="block text-[11px] font-bold uppercase tracking-wider text-slate-500 mb-1">Currency</label>
                <select value={form.currency} onChange={(e) => setForm((p) => ({ ...p, currency: e.target.value }))}
                  className="w-full h-9 px-3 border border-slate-200 rounded-xl text-[13px] focus:outline-none focus:ring-2 focus:ring-blue-500">
                  <option>INR</option><option>USD</option><option>EUR</option>
                </select>
              </div>
            </div>
            <div>
              <label className="block text-[11px] font-bold uppercase tracking-wider text-slate-500 mb-1">Duration (days)</label>
              <div className="flex gap-2">
                {[30, 90, 365].map((d) => (
                  <button key={d} onClick={() => setForm((p) => ({ ...p, duration_days: d }))}
                    className={`flex-1 h-9 rounded-xl text-[12.5px] font-semibold border transition ${form.duration_days === d ? "bg-slate-900 text-white border-slate-900" : "border-slate-200 text-slate-600 hover:border-slate-400"}`}>
                    {d === 30 ? "Monthly" : d === 90 ? "Quarterly" : "Yearly"}
                  </button>
                ))}
              </div>
            </div>
            <div>
              <label className="block text-[11px] font-bold uppercase tracking-wider text-slate-500 mb-1">Description</label>
              <input value={form.description} onChange={(e) => setForm((p) => ({ ...p, description: e.target.value }))}
                placeholder="Brief plan description"
                className="w-full h-9 px-3 border border-slate-200 rounded-xl text-[13px] focus:outline-none focus:ring-2 focus:ring-blue-500" />
            </div>
            <div className="md:col-span-2">
              <label className="block text-[11px] font-bold uppercase tracking-wider text-slate-500 mb-1">Features (one per line)</label>
              <textarea value={form.features} onChange={(e) => setForm((p) => ({ ...p, features: e.target.value }))}
                placeholder={"Up to 5 WhatsApp accounts\nUnlimited task creation\nAI classification\nPriority support"}
                rows={4}
                className="w-full px-3 py-2 border border-slate-200 rounded-xl text-[13px] focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none" />
            </div>
          </div>
          <div className="flex gap-3">
            <button onClick={() => setShowAdd(false)} className="h-9 px-4 border border-slate-200 rounded-xl text-[13px] font-semibold text-slate-600 hover:bg-slate-50 transition">Cancel</button>
            <button onClick={handleCreate} disabled={saving} className="h-9 px-5 bg-blue-600 text-white rounded-xl text-[13px] font-semibold hover:bg-blue-700 disabled:opacity-50 transition">
              {saving ? "Creating…" : "Create Plan"}
            </button>
          </div>
        </div>
      )}

      {/* Plans list */}
      {localPlans.length === 0 ? (
        <div className="py-12 text-center text-slate-400 text-sm border border-dashed border-slate-200 rounded-2xl">
          No plans yet. Create your first subscription plan above.
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {localPlans.map((plan) => (
            <div key={plan.id} className={`bg-white border rounded-2xl p-5 relative ${plan.is_active ? "border-slate-200" : "border-slate-100 opacity-60"}`}>
              <div className="flex items-start justify-between mb-3">
                <div>
                  <div className="text-[15px] font-bold text-slate-900">{plan.name}</div>
                  {plan.description && <div className="text-[12.5px] text-slate-500 mt-0.5">{plan.description}</div>}
                </div>
                <span className={`text-[10.5px] font-bold px-2 py-0.5 rounded-full border ${plan.is_active ? "bg-emerald-50 text-emerald-700 border-emerald-200" : "bg-slate-100 text-slate-500 border-slate-200"}`}>
                  {plan.is_active ? "Active" : "Inactive"}
                </span>
              </div>

              <div className="text-2xl font-black text-slate-900 mb-0.5">
                {plan.currency} {plan.price?.toLocaleString()}
              </div>
              <div className="text-[12px] text-slate-500 mb-3">
                per {plan.duration_days === 30 ? "month" : plan.duration_days === 90 ? "quarter" : plan.duration_days === 365 ? "year" : `${plan.duration_days} days`}
              </div>

              {plan.features?.length > 0 && (
                <ul className="space-y-1 mb-4">
                  {plan.features.map((f, i) => (
                    <li key={i} className="flex items-center gap-1.5 text-[12.5px] text-slate-600">
                      <CheckCircle2 size={12} className="text-emerald-500 flex-shrink-0" />
                      {f}
                    </li>
                  ))}
                </ul>
              )}

              <div className="text-[11.5px] text-slate-400 mb-3">{plan.subscriber_count || 0} subscriber{plan.subscriber_count !== 1 ? "s" : ""}</div>

              <div className="flex gap-2">
                <button onClick={() => handleToggleActive(plan)}
                  className="flex-1 h-8 border border-slate-200 rounded-xl text-[12px] font-semibold text-slate-600 hover:bg-slate-50 transition">
                  {plan.is_active ? "Deactivate" : "Activate"}
                </button>
                <button onClick={() => setEditPlan(plan)}
                  title="Edit plan"
                  className="h-8 w-8 grid place-items-center text-slate-400 hover:text-blue-600 hover:bg-blue-50 rounded-xl transition border border-slate-200">
                  <Pencil size={13} />
                </button>
                <button onClick={() => handleDelete(plan)}
                  disabled={(plan.subscriber_count || 0) > 0}
                  title={(plan.subscriber_count || 0) > 0 ? "Has active subscribers" : "Delete plan"}
                  className="h-8 w-8 grid place-items-center text-slate-400 hover:text-red-600 hover:bg-red-50 rounded-xl transition disabled:opacity-30 disabled:cursor-not-allowed border border-slate-200">
                  <Trash2 size={13} />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {editPlan && (
        <EditPlanModal
          plan={editPlan}
          onClose={() => setEditPlan(null)}
          onSaved={(updated) => {
            setLocalPlans((prev) => prev.map((p) => p.id === updated.id ? updated : p));
            setEditPlan(null);
          }}
        />
      )}
    </div>
  );
}

// ==================== Email Tab ====================
function EmailTab({ users }) {
  const [mode, setMode] = useState("all");           // all | status | user
  const [status, setStatus] = useState("trial");
  const [userId, setUserId] = useState("");
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [sending, setSending] = useState(false);
  const [history, setHistory] = useState([]);

  const loadHistory = useCallback(async () => {
    try {
      const { data } = await adminApi().get("/admin/emails");
      setHistory(data.emails || []);
    } catch { /* silent */ }
  }, []);
  useEffect(() => { loadHistory(); }, [loadHistory]);

  const recipient = mode === "all" ? "all" : mode === "status" ? `status:${status}` : `user:${userId}`;
  const recipientCount =
    mode === "all" ? users.length
    : mode === "status" ? users.filter((u) => u.subscription?.status === status).length
    : userId ? 1 : 0;
  const canSend = subject.trim() && body.trim() && recipientCount > 0 && !sending;

  const send = async () => {
    if (!canSend) return;
    setSending(true);
    try {
      const { data } = await adminApi().post("/admin/send-email", { recipient, subject, body });
      toast.success(`Queued for ${data.recipient_count} recipient${data.recipient_count !== 1 ? "s" : ""}`);
      setSubject(""); setBody("");
      setTimeout(loadHistory, 2500);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to send");
    } finally { setSending(false); }
  };

  const STATUSES = ["trial", "active", "trial_expired", "expired", "suspended"];

  return (
    <div className="grid lg:grid-cols-[1fr_340px] gap-6 items-start">
      <div className="bg-white border border-slate-200 rounded-2xl p-6">
        <h2 className="text-[15px] font-bold text-slate-900 mb-5 flex items-center gap-2">
          <Mail size={15} /> Compose email
        </h2>

        <label className="block text-[11px] font-bold uppercase tracking-wider text-slate-500 mb-2">Send to</label>
        <div className="flex flex-wrap gap-2 mb-3">
          {[["all", "All users"], ["status", "By status"], ["user", "Individual user"]].map(([m, label]) => (
            <button key={m} onClick={() => setMode(m)}
              className={`h-8 px-3 rounded-full text-[12px] font-semibold border transition ${
                mode === m ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-600 border-slate-200 hover:border-slate-400"
              }`}>
              {label}
            </button>
          ))}
        </div>

        {mode === "status" && (
          <select value={status} onChange={(e) => setStatus(e.target.value)}
            className="h-9 px-3 mb-3 border border-slate-200 rounded-xl text-[13px] focus:outline-none focus:ring-2 focus:ring-blue-500">
            {STATUSES.map((s) => <option key={s} value={s}>{STATUS_META[s]?.label || s}</option>)}
          </select>
        )}
        {mode === "user" && (
          <select value={userId} onChange={(e) => setUserId(e.target.value)}
            className="w-full h-9 px-3 mb-3 border border-slate-200 rounded-xl text-[13px] focus:outline-none focus:ring-2 focus:ring-blue-500">
            <option value="">— choose a user —</option>
            {users.map((u) => <option key={u.id} value={u.id}>{u.name} ({u.email})</option>)}
          </select>
        )}
        <div className="text-[12px] text-slate-400 mb-4">
          {recipientCount} recipient{recipientCount !== 1 ? "s" : ""}
        </div>

        <label className="block text-[11px] font-bold uppercase tracking-wider text-slate-500 mb-1.5">Subject</label>
        <input value={subject} onChange={(e) => setSubject(e.target.value)}
          placeholder="What's new in Brainydo…"
          className="w-full h-10 px-3 mb-4 border border-slate-200 rounded-xl text-[13.5px] focus:outline-none focus:ring-2 focus:ring-blue-500" />

        <label className="block text-[11px] font-bold uppercase tracking-wider text-slate-500 mb-1.5">Message</label>
        <textarea value={body} onChange={(e) => setBody(e.target.value)} rows={10}
          placeholder={"Write your message…\n\nBlank lines start a new paragraph. It's sent with clean Brainydo styling."}
          className="w-full px-3 py-2.5 mb-4 border border-slate-200 rounded-xl text-[13.5px] leading-relaxed focus:outline-none focus:ring-2 focus:ring-blue-500 resize-y" />

        <button onClick={send} disabled={!canSend}
          className="h-10 px-5 inline-flex items-center gap-2 bg-slate-900 text-white rounded-xl text-[13.5px] font-semibold hover:bg-slate-700 disabled:opacity-50 transition active:scale-95">
          {sending ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />}
          {sending ? "Queuing…" : `Send to ${recipientCount} recipient${recipientCount !== 1 ? "s" : ""}`}
        </button>
      </div>

      <div className="bg-white border border-slate-200 rounded-2xl p-5">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-[13px] font-bold text-slate-900 uppercase tracking-wider">Recent sends</h3>
          <button onClick={loadHistory} className="text-slate-400 hover:text-slate-700 transition"><RefreshCw size={13} /></button>
        </div>
        {history.length === 0 ? (
          <div className="text-[12.5px] text-slate-400 py-4 text-center">Nothing sent yet</div>
        ) : (
          <div className="space-y-3">
            {history.map((h) => (
              <div key={h.id} className="border-b border-slate-100 last:border-0 pb-3 last:pb-0">
                <div className="text-[13px] font-semibold text-slate-800 truncate">{h.subject}</div>
                <div className="text-[11.5px] text-slate-400 mt-0.5">
                  {h.recipient === "all" ? "All users" : h.recipient.replace("status:", "Status: ").replace("user:", "User ")}
                  {" · "}{fmt(h.created_at)}
                </div>
                <div className="text-[11.5px] mt-0.5">
                  {h.status === "sending"
                    ? <span className="text-amber-600 font-semibold">Sending…</span>
                    : <span className="text-slate-500">{h.sent} sent{h.failed ? <span className="text-red-500">, {h.failed} failed</span> : ""}</span>}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// ==================== Platform Settings Tab ====================
function AdminSettingsTab() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [enabled, setEnabled] = useState(false);
  const [days, setDays] = useState(90);

  useEffect(() => {
    adminApi().get("/admin/settings")
      .then(({ data }) => {
        setEnabled(!!data.inactive_delete_enabled);
        setDays(data.inactive_delete_days || 90);
      })
      .catch(() => toast.error("Could not load platform settings"))
      .finally(() => setLoading(false));
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      await adminApi().put("/admin/settings", {
        inactive_delete_enabled: enabled,
        inactive_delete_days: Number(days) || 90,
      });
      toast.success("Platform settings saved");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to save");
    } finally { setSaving(false); }
  };

  if (loading) return <div className="py-12 text-center text-slate-400 text-sm">Loading…</div>;

  return (
    <div className="max-w-2xl space-y-6">
      <PlatformWhatsAppCard />
      <RsvpInboxCard />
      <div className="bg-white border border-slate-200 rounded-2xl p-6">
        <h2 className="text-[15px] font-bold text-slate-900 mb-1.5 flex items-center gap-2">
          <SlidersHorizontal size={15} /> Data retention
        </h2>
        <p className="text-[13px] text-slate-500 mb-5">
          Automatically delete accounts (and all their data) after a period of inactivity.
          Accounts with an <strong>active paid subscription are never auto-deleted</strong>.
          Runs once a day.
        </p>

        <label className="flex items-center gap-2.5 cursor-pointer select-none mb-4">
          <input type="checkbox" checked={enabled} onChange={(e) => setEnabled(e.target.checked)}
            className="w-4 h-4 accent-slate-900" />
          <span className="text-[13.5px] font-semibold text-slate-800">Delete inactive accounts automatically</span>
        </label>

        {enabled && (
          <div className="mb-5">
            <label className="block text-[11px] font-bold uppercase tracking-wider text-slate-500 mb-1.5">
              Inactivity window (days)
            </label>
            <input type="number" min={30} max={3650} value={days} onChange={(e) => setDays(e.target.value)}
              className="w-36 h-10 px-3 border border-slate-200 rounded-xl text-[13.5px] focus:outline-none focus:ring-2 focus:ring-slate-900" />
            <p className="text-[11.5px] text-slate-400 mt-1.5">
              Minimum 30. An account is inactive when its last sign-in activity is older than this.
            </p>
            <p className="text-[11.5px] text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 mt-3 max-w-md">
              Deletion is permanent. Payment records are kept; everything else is erased and logged
              to the deletion audit trail.
            </p>
          </div>
        )}

        <button onClick={save} disabled={saving}
          className="h-10 px-5 bg-slate-900 text-white rounded-xl text-[13.5px] font-semibold hover:bg-slate-700 disabled:opacity-50 transition active:scale-95">
          {saving ? "Saving…" : "Save settings"}
        </button>
      </div>
    </div>
  );
}

// ==================== Calendar RSVP inbox ====================
function RsvpInboxCard() {
  const [st, setSt] = useState(null);
  const [checking, setChecking] = useState(false);

  useEffect(() => {
    adminApi().get("/admin/rsvp-inbox").then(({ data }) => setSt(data)).catch(() => {});
  }, []);

  const check = async () => {
    setChecking(true);
    try {
      const { data } = await adminApi().post("/admin/rsvp-inbox/check");
      setSt(data.status);
      const r = data.result || {};
      if (r.error) toast.error(`Mailbox check failed: ${r.error}`);
      else if (r.skipped) toast.info("Not configured yet — add IMAP_PASSWORD to backend/.env and restart the backend");
      else toast.success(`Checked ${r.examined} new message${r.examined === 1 ? "" : "s"} · ${r.recorded} response${r.recorded === 1 ? "" : "s"} recorded`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Check failed");
    } finally { setChecking(false); }
  };

  if (!st) return <div className="bg-white border border-slate-200 rounded-2xl p-6 text-slate-400 text-sm">Loading…</div>;
  const when = (iso) => (iso ? new Date(iso).toLocaleString() : "—");
  const badge = !st.configured
    ? ["Not configured", "text-amber-700 bg-amber-50 border-amber-200"]
    : st.last_error ? ["Error", "text-red-700 bg-red-50 border-red-200"] : ["Reading", "text-emerald-700 bg-emerald-50 border-emerald-200"];

  return (
    <div className="bg-white border border-slate-200 rounded-2xl p-6" data-testid="rsvp-inbox-card">
      <div className="flex items-start justify-between gap-4 mb-1.5">
        <h2 className="text-[15px] font-bold text-slate-900 flex items-center gap-2"><Mail size={15} /> Calendar reply inbox</h2>
        <span className={"text-[11px] font-bold uppercase tracking-wider rounded-full px-2 py-0.5 border " + badge[1]}>{badge[0]}</span>
      </div>
      <p className="text-[13px] text-slate-500 mb-4">
        Reads Yes / No / Maybe replies to Brainydo calendar invites from <span className="font-mono">{st.user}</span> and records them on the task.
        The mailbox is opened read-only: messages stay unread and nothing is moved or deleted.
      </p>
      {!st.configured && (
        <div className="text-[12.5px] text-amber-900 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 mb-4">
          Add <span className="font-mono">IMAP_PASSWORD=</span> followed by the password for {st.user} to <span className="font-mono">backend/.env</span> on the server, then restart the backend. Server {st.host}:{st.port} is already set.
        </div>
      )}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-[12.5px] mb-4">
        {[["Last check", when(st.last_poll_at)], ["Last success", when(st.last_ok_at)],
          ["New messages", st.last_examined ?? "—"], ["Answers recorded", st.total_recorded ?? 0]].map(([k, v]) => (
          <div key={k} className="rounded-lg border border-slate-100 bg-slate-50 px-3 py-2">
            <div className="text-[10.5px] font-bold uppercase tracking-wider text-slate-400">{k}</div>
            <div className="font-semibold text-slate-800 mt-0.5">{v}</div>
          </div>
        ))}
      </div>
      {st.last_error && <div className="text-[12px] text-red-700 bg-red-50 border border-red-200 rounded-lg px-3 py-2 mb-4">Last error: {st.last_error}</div>}
      <button onClick={check} disabled={checking}
        className="h-10 px-5 border border-slate-200 rounded-xl text-[13px] font-semibold hover:border-slate-900 disabled:opacity-50" data-testid="rsvp-check">
        {checking ? "Checking…" : "Check now"}
      </button>
      <span className="ml-3 text-[12px] text-slate-400">Checks automatically every {Math.round((st.poll_seconds || 180) / 60)} min.</span>
    </div>
  );
}

// ==================== Brainydo WhatsApp number (Jalpi) ====================
function PlatformWhatsAppCard() {
  const [cfg, setCfg] = useState(null);
  const [form, setForm] = useState({ enabled: false, jalpi_api_key: "", sender_number: "", language_code: "en", default_country_code: "91", templates: {} });
  const [saving, setSaving] = useState(false);
  const [templates, setTemplates] = useState(null);
  const [loadingTemplates, setLoadingTemplates] = useState(false);
  const [testPhone, setTestPhone] = useState("");
  const [testKind, setTestKind] = useState("generic");
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);

  const load = async () => {
    try {
      const { data } = await adminApi().get("/admin/platform-whatsapp");
      setCfg(data);
      setForm({ enabled: !!data.enabled, jalpi_api_key: "", sender_number: data.sender_number || "", language_code: data.language_code || "en",
        default_country_code: data.default_country_code || "91", templates: data.templates || {} });
    } catch { toast.error("Could not load the WhatsApp number settings"); }
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await adminApi().put("/admin/platform-whatsapp", form);
      setCfg((c) => ({ ...c, ...data }));
      setForm((f) => ({ ...f, jalpi_api_key: "" }));
      if (data.webhook_result && data.webhook_result !== "registered") toast.warning(`Saved. Webhook: ${data.webhook_result}`);
      else toast.success(data.webhook_result === "registered" ? "Saved — webhook registered with Jalpi" : "Saved");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to save");
    } finally { setSaving(false); }
  };

  const loadTemplates = async () => {
    setLoadingTemplates(true);
    try {
      const { data } = await adminApi().get("/admin/platform-whatsapp/templates");
      setTemplates(data.templates || []);
      if (!(data.templates || []).length) toast.info("No templates found on this number yet");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Couldn't list templates");
    } finally { setLoadingTemplates(false); }
  };

  const testSend = async () => {
    setTesting(true); setTestResult(null);
    try {
      const { data } = await adminApi().post("/admin/platform-whatsapp/test-send", { phone: testPhone, kind: testKind });
      setTestResult(data);
      if (data.ok) toast.success(`Sent as ${data.mode}${data.template ? ` (${data.template})` : ""}`);
      else toast.error(data.error || "Send failed");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Send failed");
    } finally { setTesting(false); }
  };

  if (!cfg) return <div className="bg-white border border-slate-200 rounded-2xl p-6 text-slate-400 text-sm">Loading…</div>;
  const inp = "w-full h-10 px-3 border border-slate-200 rounded-xl text-[13.5px] focus:outline-none focus:ring-2 focus:ring-slate-900";
  const approved = (templates || []).filter((t) => !t.status || String(t.status).toUpperCase() === "APPROVED");

  return (
    <div className="bg-white border border-slate-200 rounded-2xl p-6" data-testid="platform-whatsapp-card">
      <div className="flex items-start justify-between gap-4 mb-1.5">
        <h2 className="text-[15px] font-bold text-slate-900 flex items-center gap-2">
          <Radio size={15} /> Brainydo WhatsApp number (Jalpi)
        </h2>
        <label className="flex items-center gap-2 text-[13px] font-semibold text-slate-700 cursor-pointer">
          <input type="checkbox" checked={form.enabled} onChange={(e) => setForm({ ...form, enabled: e.target.checked })} className="w-4 h-4 accent-emerald-600" />
          Enabled
        </label>
      </div>
      <p className="text-[13px] text-slate-500 mb-4">
        The number Brainydo uses to send notifications to users one-to-one and to receive their replies (A12, questions). Users verify their own number under Settings → Notifications. Free-form text is only allowed within 24 h of the user's last reply; otherwise the template mapped below is sent.
      </p>

      <div className="grid sm:grid-cols-2 gap-3 mb-4">
        <div className="sm:col-span-2">
          <label className="block text-[11px] font-bold uppercase tracking-wider text-slate-500 mb-1.5">Jalpi API key {cfg.has_api_key && <span className="normal-case tracking-normal font-medium text-emerald-700">· stored ({cfg.api_key_masked})</span>}</label>
          <input type="password" value={form.jalpi_api_key} onChange={(e) => setForm({ ...form, jalpi_api_key: e.target.value })} placeholder={cfg.has_api_key ? "Leave blank to keep the current key" : "Paste the instance API key"} className={inp} autoComplete="new-password" />
        </div>
        <div>
          <label className="block text-[11px] font-bold uppercase tracking-wider text-slate-500 mb-1.5">Sender number</label>
          <input value={form.sender_number} onChange={(e) => setForm({ ...form, sender_number: e.target.value })} placeholder="e.g. 91 98765 43210" className={inp} />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="block text-[11px] font-bold uppercase tracking-wider text-slate-500 mb-1.5">Template language</label>
            <input value={form.language_code} onChange={(e) => setForm({ ...form, language_code: e.target.value })} placeholder="en" className={inp} />
          </div>
          <div>
            <label className="block text-[11px] font-bold uppercase tracking-wider text-slate-500 mb-1.5">Default country code</label>
            <input value={form.default_country_code} onChange={(e) => setForm({ ...form, default_country_code: e.target.value })} placeholder="91" className={inp} />
          </div>
        </div>
      </div>

      <div className="flex items-center justify-between mb-2">
        <div className="text-[11px] font-bold uppercase tracking-wider text-slate-500">Templates by notification kind</div>
        <button onClick={loadTemplates} disabled={loadingTemplates || !cfg.has_api_key} className="text-[12px] font-semibold text-slate-600 hover:text-slate-900 disabled:opacity-50">
          {loadingTemplates ? "Loading…" : "Load templates from Jalpi"}
        </button>
      </div>
      <div className="space-y-2 mb-4">
        {(cfg.kinds || []).map((kind) => (
          <div key={kind} className="grid sm:grid-cols-[120px_1fr] gap-2 items-center">
            <div className="text-[13px] font-semibold text-slate-800 capitalize">{kind.replace("_", " ")}</div>
            <div>
              {approved.length > 0 ? (
                <select value={form.templates?.[kind] || ""} onChange={(e) => setForm({ ...form, templates: { ...form.templates, [kind]: e.target.value } })} className={inp}>
                  <option value="">— none —</option>
                  {approved.map((t) => <option key={t.name} value={t.name}>{t.name}{t.language ? ` (${t.language})` : ""}</option>)}
                </select>
              ) : (
                <input value={form.templates?.[kind] || ""} onChange={(e) => setForm({ ...form, templates: { ...form.templates, [kind]: e.target.value } })} placeholder={`approved template name for ${kind}`} className={inp} />
              )}
              <div className="text-[11px] text-slate-400 mt-0.5">Body params: {(cfg.template_params?.[kind] || []).map((p, i) => `{{${i + 1}}} ${p}`).join(" · ")}</div>
            </div>
          </div>
        ))}
      </div>
      <p className="text-[11.5px] text-slate-500 bg-slate-50 border border-slate-200 rounded-lg px-3 py-2 mb-4">
        Register these as <strong>utility</strong> templates on the Jalpi/Meta side with exactly the parameter order shown. The <strong>generic</strong> template (one parameter with the whole message) is the safety net for any kind without its own template. Webhook URL: <span className="font-mono">{cfg.webhook_url || "(saved on first save)"}</span> — registered automatically on save.
      </p>

      <div className="flex flex-wrap items-center gap-3 mb-5">
        <button onClick={save} disabled={saving} className="h-10 px-5 bg-slate-900 text-white rounded-xl text-[13.5px] font-semibold hover:bg-slate-700 disabled:opacity-50 transition active:scale-95">
          {saving ? "Saving…" : "Save number settings"}
        </button>
        <div className="text-[12px] text-slate-500">
          {cfg.stats?.verified_users ?? 0} verified · {cfg.stats?.opted_in_users ?? 0} opted in · last 24 h: {cfg.stats?.sent_24h ?? 0} sent, {cfg.stats?.failed_24h ?? 0} failed
        </div>
      </div>

      <div className="border-t border-slate-100 pt-4">
        <div className="text-[11px] font-bold uppercase tracking-wider text-slate-500 mb-2">Send a test</div>
        <div className="flex flex-wrap gap-2">
          <input value={testPhone} onChange={(e) => setTestPhone(e.target.value)} placeholder="Phone with country code" className="h-10 px-3 border border-slate-200 rounded-xl text-[13.5px] flex-1 min-w-[200px]" />
          <select value={testKind} onChange={(e) => setTestKind(e.target.value)} className="h-10 px-3 border border-slate-200 rounded-xl text-[13.5px]">
            {(cfg.kinds || []).map((k) => <option key={k} value={k}>{k}</option>)}
          </select>
          <button onClick={testSend} disabled={testing || !testPhone.trim() || !cfg.enabled} className="h-10 px-4 border border-slate-200 rounded-xl text-[13px] font-semibold hover:border-slate-900 disabled:opacity-50">
            {testing ? "Sending…" : "Send test"}
          </button>
        </div>
        {testResult && (
          <div className={"mt-2 text-[12px] rounded-lg px-3 py-2 border " + (testResult.ok ? "bg-emerald-50 border-emerald-200 text-emerald-800" : "bg-red-50 border-red-200 text-red-700")}>
            {testResult.ok ? `Delivered to +${testResult.phone} as ${testResult.mode}${testResult.template ? ` using ${testResult.template}` : ""}.` : `Failed: ${testResult.error}`}
          </div>
        )}
      </div>
    </div>
  );
}

// ==================== Change Password Modal ====================
function ChangePasswordModal({ onClose }) {
  const [currentPw, setCurrentPw] = useState("");
  const [pw, setPw] = useState("");
  const [pw2, setPw2] = useState("");
  const [busy, setBusy] = useState(false);

  const canSubmit = currentPw && pw.length >= 6 && pw === pw2 && !busy;

  const submit = async (e) => {
    e.preventDefault();
    if (!canSubmit) return;
    setBusy(true);
    try {
      await adminApi().post("/admin/change-password", {
        current_password: currentPw,
        new_password: pw,
      });
      toast.success("Password updated");
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to change password");
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-slate-900/50 grid place-items-center px-4" onClick={onClose}>
      <div
        className="w-full max-w-sm bg-white rounded-2xl p-6 shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-[15px] font-bold text-slate-900 flex items-center gap-2">
            <KeyRound size={15} /> Change password
          </h2>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-900 transition">
            <X size={16} />
          </button>
        </div>
        <form onSubmit={submit} className="space-y-3.5">
          <div>
            <label className="block text-[11px] font-bold uppercase tracking-[0.15em] text-slate-500 mb-1.5">Current password</label>
            <input
              type="password" required autoFocus autoComplete="current-password"
              value={currentPw}
              onChange={(e) => setCurrentPw(e.target.value)}
              className="w-full h-10 px-3 border border-slate-200 rounded-xl text-[13.5px] focus:outline-none focus:ring-2 focus:ring-slate-900"
            />
          </div>
          <div>
            <label className="block text-[11px] font-bold uppercase tracking-[0.15em] text-slate-500 mb-1.5">New password</label>
            <input
              type="password" required minLength={6} autoComplete="new-password"
              placeholder="At least 6 characters"
              value={pw}
              onChange={(e) => setPw(e.target.value)}
              className="w-full h-10 px-3 border border-slate-200 rounded-xl text-[13.5px] focus:outline-none focus:ring-2 focus:ring-slate-900"
            />
          </div>
          <div>
            <label className="block text-[11px] font-bold uppercase tracking-[0.15em] text-slate-500 mb-1.5">Confirm new password</label>
            <input
              type="password" required minLength={6} autoComplete="new-password"
              value={pw2}
              onChange={(e) => setPw2(e.target.value)}
              className={
                "w-full h-10 px-3 border rounded-xl text-[13.5px] focus:outline-none focus:ring-2 focus:ring-slate-900 " +
                (pw2 && pw !== pw2 ? "border-red-400" : "border-slate-200")
              }
            />
            {pw2 && pw !== pw2 && (
              <p className="text-[11.5px] text-red-600 mt-1">Passwords don't match.</p>
            )}
          </div>
          <button
            type="submit"
            disabled={!canSubmit}
            className="w-full h-10 bg-slate-900 text-white rounded-xl text-[13.5px] font-semibold hover:bg-slate-700 disabled:opacity-50 transition active:scale-95"
          >
            {busy ? "Updating…" : "Update password"}
          </button>
        </form>
      </div>
    </div>
  );
}

// ==================== Main Admin Panel ====================
export default function Admin() {
  const navigate = useNavigate();
  const [admin, setAdmin] = useState(() => {
    try { return JSON.parse(localStorage.getItem("sw_admin") || "null"); } catch { return null; }
  });
  const [activeTab, setActiveTab] = useState("dashboard");
  const [stats, setStats] = useState(null);
  const [statsLoading, setStatsLoading] = useState(false);
  const [users, setUsers] = useState([]);
  const [usersLoading, setUsersLoading] = useState(false);
  const [plans, setPlans] = useState([]);
  const [showChangePw, setShowChangePw] = useState(false);

  useEffect(() => {
    if (!localStorage.getItem("sw_admin_token")) {
      navigate("/admin/login");
    }
  }, [navigate]);

  const loadStats = useCallback(async () => {
    setStatsLoading(true);
    try {
      const { data } = await adminApi().get("/admin/stats");
      setStats(data);
    } catch (err) {
      if (err.response?.status === 401) {
        navigate("/admin/login");
      }
    } finally { setStatsLoading(false); }
  }, [navigate]);

  const loadUsers = useCallback(async () => {
    setUsersLoading(true);
    try {
      const { data } = await adminApi().get("/admin/users");
      setUsers(data.users || []);
    } catch { /* handled */ }
    finally { setUsersLoading(false); }
  }, []);

  const loadPlans = useCallback(async () => {
    try {
      const { data } = await adminApi().get("/admin/plans");
      setPlans(data.plans || []);
    } catch { /* silent */ }
  }, []);

  useEffect(() => {
    loadStats();
    loadUsers();
    loadPlans();
  }, [loadStats, loadUsers, loadPlans]);

  const handleLogout = () => {
    localStorage.removeItem("sw_admin_token");
    localStorage.removeItem("sw_admin");
    navigate("/admin/login");
  };

  const tabs = [
    { id: "dashboard", label: "Dashboard", icon: BarChart3 },
    { id: "users",     label: "Users",     icon: Users },
    { id: "plans",     label: "Plans",     icon: CreditCard },
    { id: "email",     label: "Email",     icon: Mail },
    { id: "live",      label: "Live",      icon: Radio },
    { id: "settings",  label: "Settings",  icon: SlidersHorizontal },
  ];

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Top bar */}
      <div className="bg-slate-900 border-b border-slate-800 px-6 py-0 flex items-center justify-between h-14">
        <div className="flex items-center gap-3">
          <span className="text-white font-black text-lg tracking-tight">Brainydo</span>
          <span className="text-slate-500 text-[12px] font-semibold uppercase tracking-wider border-l border-slate-700 pl-3">Admin Panel</span>
        </div>
        <div className="flex items-center gap-4">
          <span className="text-slate-400 text-[13px]">{admin?.name || admin?.email}</span>
          <button
            onClick={() => setShowChangePw(true)}
            className="flex items-center gap-1.5 text-slate-400 hover:text-white text-[13px] transition"
          >
            <KeyRound size={14} /> Change password
          </button>
          <button onClick={handleLogout} className="flex items-center gap-1.5 text-slate-400 hover:text-white text-[13px] transition">
            <LogOut size={14} /> Logout
          </button>
        </div>
      </div>

      {showChangePw && <ChangePasswordModal onClose={() => setShowChangePw(false)} />}

      {/* Tab nav */}
      <div className="bg-white border-b border-slate-200 px-6">
        <div className="flex gap-0">
          {tabs.map(({ id, label, icon: Icon }) => (
            <button
              key={id}
              onClick={() => setActiveTab(id)}
              className={`flex items-center gap-2 px-4 py-3.5 text-[13.5px] font-semibold border-b-2 transition ${
                activeTab === id
                  ? "border-slate-900 text-slate-900"
                  : "border-transparent text-slate-500 hover:text-slate-800 hover:border-slate-300"
              }`}
            >
              <Icon size={15} />
              {label}
              {id === "users" && users.length > 0 && (
                <span className="ml-1 bg-slate-100 text-slate-600 text-[11px] font-bold px-1.5 py-0.5 rounded-full">
                  {users.length}
                </span>
              )}
              {id === "live" && (
                <span className="ml-1 w-2 h-2 rounded-full bg-emerald-500 animate-pulse" />
              )}
            </button>
          ))}
        </div>
      </div>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-6 py-6">
        {activeTab === "dashboard" && <DashboardTab stats={stats} loading={statsLoading} />}
        {activeTab === "users"     && <UsersTab users={users} plans={plans} loading={usersLoading} onRefresh={() => { loadUsers(); loadStats(); }} />}
        {activeTab === "plans"     && <PlansTab plans={plans} onRefresh={loadPlans} />}
        {activeTab === "email"     && <EmailTab users={users} />}
        {activeTab === "live"      && <LiveTab />}
        {activeTab === "settings"  && <AdminSettingsTab />}
      </div>
    </div>
  );
}
